import {useQueryClient} from "@tanstack/react-query";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {SEO} from "@/components";
import {PrimaryButton} from "@/components/Button";
import {prefetchPaginatedGames} from "@/hooks/games/paginatedGames.query";
import {useGetHomeSections} from "@/hooks/homeSections/useGetHomeSections";
import AppBanners from "@/pages/public/Home/components/AppBanners";
import ModalSessionInfo from "@/pages/public/Home/components/ModalSessionInfo";
import PaymentProfileCard from "@/pages/public/Home/components/PaymentProfileCard";
import DynamicHomeLayout from "@/pages/public/Home/DynamicHomeLayout";
import LegacyHomeLayout from "@/pages/public/Home/LegacyHomeLayout";
import {filterBannersByPage, useAppConfig} from "@/store/useAppConfig.store";
import {useBalanceStore} from "@/store/useBalance.store";
import {dropStorage, setStorage} from "@/store/util.store";
import {ErrorBoundaryHome} from "../../../components/ErrorBoundaryHandler/ErrorBoundaryHome";
import {useGetCustomBoards} from "../../../hooks/gamesList/useGetCustomBoards";
import {useAuthStore} from "../../../store/useAuth.store";

import {isNativeShell} from "@/services/nativeBridge.service";
import {useMiniGames} from "../../../store/useMiniGames";
import styles from "./home.module.css";

/** Disables scroll-driven parallax/reveal on Home (all clients). */
const HOME_DISABLE_MOTION = true;
const VE_OFFSET = -4;
const HOME_LAYOUT_ENV_FLAG = import.meta.env.VITE_HOME_LAYOUT_ENABLED === "true";

const getNowVenezuela = () => {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60_000;
  return new Date(utc + VE_OFFSET * 3_600_000);
};

const getNextMonday = () => {
  const ve = getNowVenezuela();
  const day = ve.getDay();
  const daysUntilMonday = day === 0 ? 1 : day === 1 ? 7 : 8 - day;
  const nextMonday = new Date(ve);
  nextMonday.setDate(ve.getDate() + daysUntilMonday);
  nextMonday.setHours(0, 0, 0, 0);
  return nextMonday.getTime();
};

const formatCountdown = ms => {
  if (ms <= 0) return {days: 0, hours: 0, minutes: 0};
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  return {days, hours, minutes};
};

const getNextDailyPromoStart = veNow => {
  const nextStart = new Date(veNow);
  nextStart.setSeconds(0, 0);

  const day = veNow.getDay(); // 0: Sun ... 4: Thu
  const hour = veNow.getHours();
  const isThursday = day === 4;

  if (!isThursday && hour < 20) {
    nextStart.setHours(20, 0, 0, 0);
    return nextStart.getTime();
  }

  nextStart.setDate(nextStart.getDate() + 1);
  nextStart.setHours(20, 0, 0, 0);

  while (nextStart.getDay() === 4) {
    nextStart.setDate(nextStart.getDate() + 1);
  }

  return nextStart.getTime();
};

const getNextThursdayPromoStart = veNow => {
  const nextStart = new Date(veNow);
  nextStart.setSeconds(0, 0);

  const day = veNow.getDay(); // 4 = Thursday
  const hour = veNow.getHours();

  if (day === 4 && hour < 20) {
    nextStart.setHours(20, 0, 0, 0);
    return nextStart.getTime();
  }

  let daysUntilThursday = (4 - day + 7) % 7;
  if (daysUntilThursday === 0) {
    daysUntilThursday = 7;
  }

  nextStart.setDate(nextStart.getDate() + daysUntilThursday);
  nextStart.setHours(20, 0, 0, 0);
  return nextStart.getTime();
};

const HomePage = () => {
  const {t} = useTranslation();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleAuthForm = useAuthStore(state => state.handleAuthForm);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);
  const getMiniGames = useMiniGames(state => state.getMiniGames);
  const allGames = useMiniGames(state => state.list);

  const {data: customBoards} = useGetCustomBoards();
  const setStoreSearch = useMiniGames(state => state.setSearch);
  const providerOptions = useMiniGames(state => state.providerOptions);
  const classOptions = useMiniGames(state => state.classOptions);
  const allProviderOptions = useMiniGames(state => state.allProviderOptions);
  const allClassOptions = useMiniGames(state => state.allClassOptions);
  const ensureFilterMetadata = useMiniGames(state => state.ensureFilterMetadata);
  const filterProviderOptions = allProviderOptions.length > 0 ? allProviderOptions : providerOptions;
  const filterClassOptions = allClassOptions.length > 0 ? allClassOptions : classOptions;
  const allWebBanners = useAppConfig(state => state.webBanners);
  const homeLayoutFromConfig = useAppConfig(state => state.featureFlags.homeLayoutEnabled);
  const homeLayoutEnabled = HOME_LAYOUT_ENV_FLAG || homeLayoutFromConfig;
  const {
    sections: homeSections,
    isPending: isHomeSectionsPending,
    isError: isHomeSectionsError,
  } = useGetHomeSections(homeLayoutEnabled);
  const heroBanners = useMemo(() => filterBannersByPage(allWebBanners, "home"), [allWebBanners]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [homeSearchTerm, setHomeSearchTerm] = useState("");
  const [homeActiveCategory, setHomeActiveCategory] = useState("");
  const [promoCountdown, setPromoCountdown] = useState(() => formatCountdown(getNextMonday() - Date.now()));
  const [dailyPromoCountdown, setDailyPromoCountdown] = useState(() =>
    formatCountdown(getNextDailyPromoStart(getNowVenezuela()) - Date.now())
  );
  const [thursdayPromoCountdown, setThursdayPromoCountdown] = useState(() =>
    formatCountdown(getNextThursdayPromoStart(getNowVenezuela()) - Date.now())
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sectionsRef = useRef(null);
  const atmosphereRef = useRef(null);
  const zoneRef = useRef(null);

  useEffect(() => {
    if (allGames.length === 0) {
      getMiniGames("", "");
      return;
    }
    ensureFilterMetadata();
  }, [allGames.length, getMiniGames, ensureFilterMetadata]);

  useEffect(() => {
    const interval = setInterval(() => {
      const ms = getNextMonday() - Date.now();
      setPromoCountdown(formatCountdown(ms));

      const nextDailyStart = getNextDailyPromoStart(getNowVenezuela());
      setDailyPromoCountdown(formatCountdown(nextDailyStart - Date.now()));

      const nextThursdayStart = getNextThursdayPromoStart(getNowVenezuela());
      setThursdayPromoCountdown(formatCountdown(nextThursdayStart - Date.now()));
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (HOME_DISABLE_MOTION || isNativeShell()) return;

    const container = sectionsRef.current;
    if (!container) return;

    const sections = container.querySelectorAll(":scope > *");
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("premium-reveal");
            observer.unobserve(entry.target);
          }
        }
      },
      {threshold: 0.08}
    );

    for (const section of sections) {
      section.style.opacity = "0";
      section.style.transform = "translateY(24px)";
      observer.observe(section);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (authStatus === "unauthenticated") {
      setStorage("hasModalBeenShown", false);
    }

    if (window.location.pathname === "/") {
      dropStorage("isMobileLoginUser");
    }
  }, [authStatus]);

  useEffect(() => {
    if (HOME_DISABLE_MOTION || isNativeShell()) return;

    const el = atmosphereRef.current;
    const zone = zoneRef.current;
    if (!el) return;

    let ticking = false;
    let lastZone = "";

    const getZone = progress => {
      if (progress < 0.15) return "hero";
      if (progress < 0.6) return "games";
      if (progress < 0.82) return "sports";
      return "footer";
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const clampedY = Math.min(Math.round(y), Math.max(max, 0));
        const progress = max > 0 ? Math.min(clampedY / max, 1) : 0;

        el.style.setProperty("--scroll-progress", progress.toFixed(4));
        el.style.setProperty("--parallax-y", String(clampedY));

        const currentZone = getZone(progress);
        if (zone && currentZone !== lastZone) {
          zone.dataset.zone = currentZone;
          lastZone = currentZone;
        }

        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, {passive: true});
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (HOME_DISABLE_MOTION || isNativeShell()) return;

    const container = sectionsRef.current;
    if (!container) return;

    const glowElements = container.querySelectorAll(`[data-glow]`);
    if (glowElements.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          entry.target.dataset.visible = entry.isIntersecting ? "true" : "false";
        }
      },
      {rootMargin: "100px 0px", threshold: 0.01}
    );

    for (const el of glowElements) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const filteredHomeGames = useMemo(() => {
    let games = allGames;

    if (homeActiveCategory) {
      games = games.filter(g => g.class === homeActiveCategory);
    }
    if (homeSearchTerm.trim().length >= 3) {
      const term = homeSearchTerm.trim().toLowerCase();
      games = games.filter(
        game =>
          game.name.toLowerCase().includes(term) ||
          game.provider.toLowerCase().includes(term) ||
          Boolean(game.provider_display_name?.toLowerCase().includes(term))
      );
    }

    return games;
  }, [allGames, homeSearchTerm, homeActiveCategory]);

  const isHomeSearching = homeSearchTerm.trim().length >= 3 || Boolean(homeActiveCategory);
  const shouldHideCategoryPills = homeSearchTerm.trim().length >= 3 || Boolean(homeActiveCategory);
  const isMonday = getNowVenezuela().getDay() === 1;
  const veNow = getNowVenezuela();
  const isDailyPromoActive = veNow.getDay() !== 4 && veNow.getHours() >= 20 && veNow.getHours() < 21;
  const isThursdayPromoActive = veNow.getDay() === 4 && veNow.getHours() >= 20 && veNow.getHours() < 21;
  const countdownText = useMemo(() => {
    const {days, hours, minutes} = promoCountdown;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, [promoCountdown]);
  const dailyPromoCountdownText = useMemo(() => {
    const {days, hours, minutes} = dailyPromoCountdown;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, [dailyPromoCountdown]);
  const thursdayPromoCountdownText = useMemo(() => {
    const {days, hours, minutes} = thursdayPromoCountdown;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, [thursdayPromoCountdown]);

  const handleHomeSearch = useCallback(value => {
    setHomeSearchTerm(value);
  }, []);

  const handleHomeSearchSubmit = useCallback(
    value => {
      if (value.trim()) {
        setStoreSearch(value);
        navigate("/gameslist/all");
      }
    },
    [setStoreSearch, navigate]
  );

  const handleHomeProviderChange = useCallback(
    providerKey => {
      if (!providerKey) return;

      setStoreSearch("");
      void prefetchPaginatedGames(queryClient, {provider: providerKey, class: "", search: ""});
      navigate("/gameslist/all#all-games", {state: {activeProvider: providerKey}});
    },
    [navigate, queryClient, setStoreSearch]
  );

  const handleRegisterPromoClick = useCallback(() => {
    if (authStatus === "authenticated") {
      navigate("/deposit");
      return;
    }

    handleAuthForm("registration");
    handleShowAuthModal();
  }, [authStatus, handleAuthForm, handleShowAuthModal, navigate]);

  const handleOpenGame = useCallback(
    (gameId, provider) => {
      if (authStatus !== "authenticated") {
        handleShowAuthModal();
        return;
      }
      navigate(`/games/${provider.toLowerCase()}/${gameId}`);
    },
    [authStatus, handleShowAuthModal, navigate]
  );

  const handleOk = () => {
    setIsModalOpen(false);
    navigate("/raffles");
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const homeLayoutCtx = useMemo(
    () => ({
      t,
      authStatus,
      styles,
      heroBanners,
      filteredHomeGames,
      isHomeSearching,
      shouldHideCategoryPills,
      filterProviderOptions,
      filterClassOptions,
      homeActiveCategory,
      homeSearchTerm,
      customBoards,
      handleHomeSearch,
      handleHomeSearchSubmit,
      handleHomeProviderChange,
      handleOpenGame,
      setHomeActiveCategory,
      setStoreSearch,
      navigate,
      isMonday,
      isDailyPromoActive,
      isThursdayPromoActive,
      countdownText,
      dailyPromoCountdownText,
      thursdayPromoCountdownText,
      handleRegisterPromoClick,
      handleBankAccountModal,
      sectionsRef,
    }),
    [
      t,
      authStatus,
      heroBanners,
      filteredHomeGames,
      isHomeSearching,
      shouldHideCategoryPills,
      filterProviderOptions,
      filterClassOptions,
      homeActiveCategory,
      homeSearchTerm,
      customBoards,
      handleHomeSearch,
      handleHomeSearchSubmit,
      handleHomeProviderChange,
      handleOpenGame,
      setHomeActiveCategory,
      setStoreSearch,
      navigate,
      isMonday,
      isDailyPromoActive,
      isThursdayPromoActive,
      countdownText,
      dailyPromoCountdownText,
      thursdayPromoCountdownText,
      handleRegisterPromoClick,
      handleBankAccountModal,
    ]
  );

  const useDynamicHomeLayout =
    homeLayoutEnabled && !isHomeSectionsPending && !isHomeSectionsError && homeSections.length > 0;

  return (
    <>
      <SEO
        title="Randrush - Casino, Sports, Lotteries and more"
        description="Randrush - Your online betting platform. Casino, sports, lotteries, bingo, horses and more. Where the winner is you!"
        path="/"
      />
      {authStatus === "authenticated" && (
        <>
          <ModalSessionInfo />
          <PaymentProfileCard />
          <AppBanners />
          {isModalOpen && (
            <div
              className={styles.raffleModalOverlay}
              onClick={handleCancel}
              onKeyDown={e => e.key === "Escape" && handleCancel()}
              role="dialog"
              aria-modal="true"
              aria-label="Rifa Fin de Año"
            >
              <div
                className={styles.raffleModalContent}
                onClick={e => e.stopPropagation()}
              >
                <button
                  className={styles.raffleModalClose}
                  onClick={handleCancel}
                  aria-label="Cerrar"
                  type="button"
                >
                  &times;
                </button>
                <img
                  className={styles.raffleModalImage}
                  onClick={handleOk}
                  onKeyUp={e => {
                    if (e.key === "Enter" || e.key === " ") handleOk();
                  }}
                  alt="Rifa Fin de Año"
                  src="https://images.cdn.randrush.com/randrush-rifa-placeholder.jpeg"
                />
                <PrimaryButton
                  onClick={handleOk}
                  className={styles.raffleModalBtn}
                >
                  Compra YA!
                </PrimaryButton>
              </div>
            </div>
          )}
        </>
      )}

      <div
        className={`${styles.homeAtmosphere} homeAtmosphere`}
        ref={atmosphereRef}
      >
        {!isNativeShell() && (
          <>
            <div
              className={`${styles.atmosphereDeep} atmosphereDeep`}
              aria-hidden="true"
            />
            <div
              className={`${styles.atmosphereCanvas} atmosphereCanvas`}
              aria-hidden="true"
            />
            <div
              className={`${styles.atmosphereHero} atmosphereHero`}
              aria-hidden="true"
            />
            <div
              className={`${styles.atmosphereOrbs} atmosphereOrbs`}
              aria-hidden="true"
            />
            <div
              className={`${styles.atmosphereZone} atmosphereZone`}
              ref={zoneRef}
              data-zone="hero"
              aria-hidden="true"
            />
          </>
        )}
        <ErrorBoundaryHome>
          {useDynamicHomeLayout ? (
            <DynamicHomeLayout
              sections={homeSections}
              ctx={homeLayoutCtx}
            />
          ) : (
            <LegacyHomeLayout ctx={homeLayoutCtx} />
          )}
        </ErrorBoundaryHome>
      </div>
    </>
  );
};

export default HomePage;
